import { Controller } from "@hotwired/stimulus";
import TomSelect from "tom-select";

export default class extends Controller {
  static values = {
    options: Object,
    cssClasses: Object,
    plugins: Array,
    mode: String,
    dependentUrl: String,
    dependentTarget: String,
    noResultsOption: String,
    noResultsText: { type: String, default: "No results found" },
    selectOptionText: { type: String, default: 'Select "%{option}"' },
    validationRequiredText: { type: String, default: "Please fill out this field." },
    selectCarMakeFirstText: { type: String, default: "Select car make first" },
    noModelsAvailableText: { type: String, default: "No models available" },
    selectModelText: { type: String, default: "Select model" },
  };

  connect() {
    this._numericBeforeInputHandler = this.handleNumericBeforeInput.bind(this);
    this._numericPasteHandler = this.handleNumericPaste.bind(this);
    this.initializeTomSelect();
  }

  disconnect() {
    this.disableNumericOnlyInput();
    if (this._noResultsClickHandler) {
      document.removeEventListener("click", this._noResultsClickHandler);
    }
    if (this.tomSelect) {
      this.tomSelect.destroy();
      this.tomSelect = null;
    }
  }

  initializeTomSelect() {
    const isMulti = this.modeValue === "multi";
    const isModelSearch = this.modeValue === "model-search";
    const maxLength = this.element.maxLength;

    const defaultConfig = {
      plugins: ["restore_on_backspace"],
      create: false,
      sortField: [{ field: "$order" }, { field: "$score" }],
      maxOptions: null,
      controlInput: "<input>",
      searchField: ["text", "value"],
      maxItems: isMulti ? null : 1,
    };

    const config = { ...defaultConfig, ...this.optionsValue };

    if (isMulti) {
      Object.assign(config, {
        create: true,
        persist: false,
        delimiter: ",",
        createOnBlur: true,
        openOnFocus: false,
        render: {
          option: () => "", // hide dropdown text
          no_results: () => "", // hide "No results found"
          item: function (data, escape) {
            return `<div class="custom-tomselect-item">${escape(
              data.text
            )}</div>`;
          },
        },
        onKeyDown: function (evt) {
          const value = this.inputState().value.trim();
          if (!value) return;
          if (evt.keyCode === 188 || evt.keyCode === 13) {
            evt.preventDefault();
            this.createItem();
          }
        },
        onBeforeCreate: function (input) {
          const cleaned = input
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean)
            .join(", ");
          if (!cleaned) return false;
          if (maxLength > 0 && cleaned.length > maxLength) return false;
          return { value: cleaned, text: cleaned };
        },
      });
    }

    if (this.hasCssClassesValue && !isMulti) {
      config.render = { ...config.render, ...this.buildCustomRenderers() };
    }

    if (this.hasNoResultsOptionValue && !isMulti) {
      const optionLabel = this.noResultsOptionValue;
      config.render = {
        ...config.render,
        no_results: (data, escape) => `
          <div class="flex flex-col">
            <div class="text-gray-500 text-sm px-3 py-2">${this.noResultsTextValue}</div>
            <div class="text-gray-500 text-sm px-3 py-2 cursor-pointer hover:bg-gray-50 border-t border-gray-100 flex items-center gap-2 no-results-option"
                 data-no-results-option="${escape(optionLabel)}">
              <span>+</span>
              <span>${this.selectOptionTextValue.replace('%{option}', escape(optionLabel))}</span>
            </div>
          </div>`,
      };
    }

    this.tomSelect = new TomSelect(this.element, config);

    if (maxLength > 0 && this.tomSelect.control_input) {
      this.tomSelect.control_input.setAttribute("maxlength", String(maxLength));
    }

    // Browsers auto-select the first option in single selects. For controls
    // explicitly opting in, clear an initial empty value so placeholder shows.
    const clearInitialEmptyValue =
      this.element.dataset.clearInitialEmptyValue === "true";
    if (
      clearInitialEmptyValue &&
      !isMulti &&
      this.tomSelect.getValue() === ""
    ) {
      const ph =
        this.element.getAttribute("placeholder") ||
        this.tomSelect.settings.placeholder ||
        "";
      this.tomSelect.clear(true);
      this.tomSelect.settings.placeholder = ph;
      this.tomSelect.inputState();
    }

    this.tomSelect.on("item_add", () => {
      const input = this.tomSelect.control.querySelector("input");
      if (input) {
        input.placeholder = "";
        this.tomSelect.settings.placeholder = "";
      }
    });

    if (this.tomSelect.items.length > 0) {
      const input = this.tomSelect.control.querySelector("input");
      if (input) {
        input.placeholder = "";
        this.tomSelect.settings.placeholder = "";
      }
    }

    const inputEl = this.tomSelect.control_input;
    if (this.element.id === "country-select" && inputEl) {
      inputEl.addEventListener("focus", () => {
        inputEl.value = inputEl.value.trim();
      });
      inputEl.addEventListener("keydown", () => {
        inputEl.value = inputEl.value.replace(/\s+/g, " ").trim();
      });
    }

    if (isMulti) {
      const dropdown = this.tomSelect.dropdown;
      if (dropdown) {
        dropdown.style.display = "none";
        const observer = new MutationObserver(() => {
          dropdown.style.display = "none";
        });
        observer.observe(dropdown, {
          attributes: true,
          attributeFilter: ["style", "class"],
        });
      }
    }

    this.applyCssClasses();

    // Server-rendered error state is applied before this controller
    // reconnects after a failed submit, so re-apply the visual mark on the
    // freshly created control.
    if (this.element.matches('[data-form-error="true"], [aria-invalid="true"]')) {
      this.tomSelect.control.classList.add("border-red-500", "ring-2", "ring-red-200");
    }

    this.enableNumericOnlyInput();

    if (this.hasNoResultsOptionValue && !isMulti) {
      this._noResultsClickHandler = (event) => {
        const badge = event.target.closest("[data-no-results-option]");
        if (!badge) return;
        const dropdown = badge.closest(`[id="${this.element.id}-ts-dropdown"]`);
        if (!dropdown) return;
        const value = badge.dataset.noResultsOption;
        if (value && this.tomSelect) {
          this.tomSelect.setValue(value);
          this.tomSelect.close();
        }
      };
      document.addEventListener("click", this._noResultsClickHandler);
    }

    if (isModelSearch) {
      this.element.addEventListener("change", (event) =>
        this.updateDependentSelect(event)
      );
      this.handlePlaceholderBehavior(isModelSearch);
    }

    this.handlePlaceholderBehavior(isMulti);

    // Add validation support for required fields
    this.setupValidation();
  }

  numericOnlyEnabled() {
    return this.element?.dataset?.numericOnlyValue === "true";
  }

  enableNumericOnlyInput() {
    if (!this.numericOnlyEnabled() || !this.tomSelect?.control_input) return;
    const inputEl = this.tomSelect.control_input;
    this._numericInputEl = inputEl;
    inputEl.setAttribute("inputmode", "numeric");
    inputEl.addEventListener("beforeinput", this._numericBeforeInputHandler);
    inputEl.addEventListener("paste", this._numericPasteHandler);
  }

  disableNumericOnlyInput() {
    if (!this._numericInputEl) return;
    this._numericInputEl.removeEventListener(
      "beforeinput",
      this._numericBeforeInputHandler
    );
    this._numericInputEl.removeEventListener("paste", this._numericPasteHandler);
    this._numericInputEl = null;
  }

  handleNumericBeforeInput(event) {
    if (!this.numericOnlyEnabled()) return;
    const t = event.inputType || "";
    if (
      t === "deleteContentBackward" ||
      t === "deleteContentForward" ||
      t === "deleteByCut" ||
      t === "deleteCompositionText"
    ) {
      return;
    }
    if (event.data == null || event.data === "") return;
    if (!/^\d+$/.test(event.data)) event.preventDefault();
  }

  handleNumericPaste(event) {
    if (!this.numericOnlyEnabled()) return;
    const text = event.clipboardData?.getData("text") ?? "";
    if (text === "") return;
    if (!/^\d+$/.test(text)) event.preventDefault();
  }

  setupValidation() {
    // Listen for validation attempts on the original select element
    this.element.addEventListener('invalid', (event) => {
      event.preventDefault();
      this.showValidationMessage();
    });

    // Clear validation message when user selects a value
    this.tomSelect.on('change', () => {
      if (this.element.value) {
        this.clearValidationMessage();
      }
    });
  }

  showValidationMessage() {
    if (!this.element.hasAttribute('required')) return;

    const control = this.tomSelect.control;

    // Add visual feedback
    control.classList.add('border-red-500', 'ring-2', 'ring-red-200');

    // Create or update error message
    let errorMsg = control.parentElement.querySelector('.tom-select-error');
    if (!errorMsg) {
      errorMsg = document.createElement('div');
      errorMsg.className = 'tom-select-error text-red-600 text-sm mt-1';
      errorMsg.textContent = this.validationRequiredTextValue;
      control.parentElement.appendChild(errorMsg);
    }
  }

  clearValidationMessage() {
    const control = this.tomSelect.control;
    control.classList.remove('border-red-500', 'ring-2', 'ring-red-200');

    const errorMsg = control.parentElement.querySelector('.tom-select-error');
    if (errorMsg) {
      errorMsg.remove();
    }

    // Clear the custom validity
    this.element.setCustomValidity('');
  }

  buildCustomRenderers() {
    const renderers = {};
    const cssClasses = this.cssClassesValue;
    if (cssClasses.option) {
      renderers.option = (item, escape) => `
        <div class="${cssClasses.option}">
          <span>${escape(item.text)}</span>
        </div>`;
    }
    if (cssClasses.item) {
      renderers.item = (item, escape) => `
        <div class="${cssClasses.item}">
          <span>${escape(item.text)}</span>
        </div>`;
    }
    return renderers;
  }

  applyCssClasses() {
    if (!this.hasCssClassesValue) return;
    const css = this.cssClassesValue;
    if (css.wrapper)
      this.tomSelect.wrapper.classList.add(...css.wrapper.split(" "));
    if (css.control)
      this.tomSelect.control.classList.add(...css.control.split(" "));
    if (css.input)
      this.tomSelect.control
        .querySelector("input")
        ?.classList.add(...css.input.split(" "));
    if (css.dropdown)
      this.tomSelect.dropdown.classList.add(...css.dropdown.split(" "));
  }

  handlePlaceholderBehavior(isMulti) {
    if (isMulti) return;

    const inputEl = this.tomSelect.control.querySelector("input");
    if (!inputEl) return;

    inputEl.addEventListener("focus", () => {
      this.tomSelect.settings.placeholder = "";
      inputEl.placeholder = "";
    });

    inputEl.addEventListener("blur", () => {
      setTimeout(() => {
        const hasItems = this.tomSelect.items.length > 0;
        const hasItemDiv = this.tomSelect.control.querySelector("div.item");

        if (!hasItems && !hasItemDiv) {
          const ph = this.element.getAttribute("placeholder") || "";
          this.tomSelect.settings.placeholder = ph;
          inputEl.placeholder = ph;
        } else {
          this.tomSelect.settings.placeholder = "";
          inputEl.placeholder = "";
        }
      }, 0);
    });

    this.tomSelect.on("item_remove", () => {
      if (this.tomSelect.items.length === 0) {
        const ph = this.element.getAttribute("placeholder") || "";
        this.tomSelect.settings.placeholder = ph;
        this.tomSelect.input.placeholder = ph;
      }
    });

    const maxItemsValue = this.tomSelect.settings.maxItems;
    if (maxItemsValue === 1)
      this.tomSelect.on("type", () => this.tomSelect.clear());
  }

  async updateDependentSelect(event) {
    const selectedId = event.target.value;
    const dependentElement = document.querySelector(this.dependentTargetValue);
    if (!dependentElement) return;

    const dependentSelect = dependentElement.tomselect;
    if (!dependentSelect) return;

    dependentSelect.clearOptions();
    dependentSelect.disable();

    const inputEl = dependentSelect.control_input;

    if (!selectedId) {
      dependentSelect.settings.placeholder = this.selectCarMakeFirstTextValue;
      if (inputEl) inputEl.placeholder = this.selectCarMakeFirstTextValue;
      dependentSelect.refreshOptions(false);
      dependentSelect.setValue("");
      return;
    }

    try {
      const response = await fetch(
        this.dependentUrlValue.replace(":id", selectedId)
      );
      const data = await response.json();

      dependentSelect.clearOptions();

      if (data.length === 0) {
        dependentSelect.addOption({ value: "", text: this.noModelsAvailableTextValue });
      } else {
        data.forEach((model) => {
          dependentSelect.addOption({ value: model.id, text: model.name });
        });
      }

      dependentSelect.enable();
      dependentSelect.settings.placeholder = this.selectModelTextValue;
      if (inputEl) inputEl.placeholder = this.selectModelTextValue;
      dependentSelect.refreshOptions(false);
      dependentSelect.setValue("");
    } catch (error) {
      console.error("Error loading models:", error);
    }
  }
}
